import React, { useCallback, useMemo } from 'react'
import type { FieldStringProps as StringFieldProps } from '../String'
import StringField from '../String'
import useTranslation from '../../hooks/useTranslation'
import type { Validator, ValidatorWithCustomValidators } from '../../types'
import withComponentMarkers from '../../../../shared/helpers/withComponentMarkers'

export type OrganizationNumberValidator = ValidatorWithCustomValidators<
  string,
  {
    organizationNumberValidator: Validator<string>
  }
>
export type FieldOrganizationNumberProps = Omit<
  StringFieldProps,
  'onBlurValidator'
> & {
  validate?: boolean
  omitMask?: boolean
  onBlurValidator?: OrganizationNumberValidator | false
}

function OrganizationNumber(props: FieldOrganizationNumberProps) {
  const translations = useTranslation().OrganizationNumber
  const { errorOrgNo, errorOrgNoLength, errorRequired, label } =
    translations

  const errorMessages = useMemo(
    () => ({
      'Field.errorRequired': errorRequired,
      'Field.errorPattern': errorOrgNo,
      ...props.errorMessages,
    }),
    [errorRequired, errorOrgNo, props.errorMessages]
  )

  const organizationNumberValidator = useCallback(
    (value: string) => {
      if (value !== undefined) {
        const orgNoIs9Digits = value?.length === 9

        if (!orgNoIs9Digits) {
          return Error(errorOrgNoLength)
        }
        if (orgNoIs9Digits && !isValidOrgNumber(value)) {
          return Error(errorOrgNo)
        }
      }

      return undefined
    },
    [errorOrgNo, errorOrgNoLength]
  )

  const {
    validate = true,
    omitMask,
    onChangeValidator,
    onBlurValidator = organizationNumberValidator,
    label: labelProp,
    width,
  } = props

  const mask = useMemo(
    () =>
      omitMask
        ? [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]
        : [/\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/],
    [omitMask]
  )

  const onBlurValidatorToUse =
    onBlurValidator === false ? undefined : onBlurValidator

  const StringFieldProps: StringFieldProps = {
    ...props,
    className: 'dnb-forms-field-organization-number',
    label: labelProp ?? label,
    errorMessages,
    mask,
    width: width ?? 'medium',
    inputMode: 'numeric',
    onChangeValidator: validate ? onChangeValidator : undefined,
    // @ts-expect-error - strictFunctionTypes
    onBlurValidator: validate ? onBlurValidatorToUse : undefined,
    exportValidators: { organizationNumberValidator },
  }

  return <StringField {...StringFieldProps} />
}

/**
 * Source:
 * www.brreg.no/om-oss/registrene-vare/om-enhetsregisteret/organisasjonsnummeret/
 */
function isValidOrgNumber(digits: string) {
  if (parseFloat(digits) === 0) {
    return false
  }
  let checkDigit = 2
  let sum = 0

  for (let i = digits.length - 2; i >= 0; --i) {
    sum += parseInt(digits.charAt(i), 10) * checkDigit

    checkDigit += 1

    if (checkDigit > 7) {
      checkDigit = 2
    }
  }

  const result = 11 - (sum % 11)
  const finalCheckDigit = result === 11 ? 0 : result

  return parseInt(digits.charAt(digits.length - 1), 10) === finalCheckDigit
}

withComponentMarkers(OrganizationNumber, {
  _supportsSpacingProps: true,
})

export default OrganizationNumber
